// Insurance Service - CRUD for teamversicherungen table
// Stores versioned insurance rate parameters (AHV, FAK, SUVA, KTG, PK)
// Only GL members should call these functions. Access control at component level.
// No console logs for insurance data (security).

const INSURANCE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "teamversicherungen",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${INSURANCE_CONFIG.storageAccount}.table.core.windows.net/${INSURANCE_CONFIG.tableName}`;

const generateInsuranceId = () =>
  `ins_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ensureTableExists = async () => {
  try {
    const url = `https://${INSURANCE_CONFIG.storageAccount}.table.core.windows.net/Tables?${INSURANCE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: INSURANCE_CONFIG.tableName }),
    });
    return response.ok || response.status === 409;
  } catch {
    return false;
  }
};

// Insurance types with their field definitions
const INSURANCE_TYPES = {
  ahv: { label: "AHV/IV/EO/ALV", fields: ["SatzAN", "SatzAG"] },
  fak: { label: "FAK", fields: ["SatzAG", "Kinderzulage", "Ausbildungszulage", "MinEinkommenMonat"] },
  suva: { label: "SUVA", fields: ["NbuAN_M", "NbuAN_W", "NbuAG_M", "NbuAG_W", "BuAG_M", "BuAG_W", "MaxVersicherterLohn"] },
  ktg: { label: "Krankentaggeld", fields: ["SatzAN_M", "SatzAN_W", "SatzAG_M", "SatzAG_W", "MaxVersicherterLohn"] },
  pk: { label: "Pensionskasse", fields: ["BerechnungsModus", "Koordinationsabzug", "Eintrittsschwelle", "MaxVersicherterLohn", "AnteilAG", "AlterstabelleMaenner", "AlterstabelleFrauen"] },
};

export { INSURANCE_TYPES };

// Map Azure entity to JS object based on type
const mapEntityToRate = (item) => {
  const type = item.PartitionKey;
  const base = {
    id: item.RowKey,
    type,
    gueltigAb: item.GueltigAb || "",
    zahlungsrhythmus: item.Zahlungsrhythmus || "monatlich",
    jaehrlichEndMonat: item.JaehrlichEndMonat || 12,
    bemerkung: item.Bemerkung || "",
    createdAt: item.CreatedAt || "",
    updatedAt: item.UpdatedAt || "",
  };

  switch (type) {
    case "ahv":
      return { ...base, satzAN: parseFloat(item.SatzAN) || 0, satzAG: parseFloat(item.SatzAG) || 0 };
    case "fak":
      return {
        ...base,
        satzAG: parseFloat(item.SatzAG) || 0,
        kinderzulage: parseFloat(item.Kinderzulage) || 0,
        ausbildungszulage: parseFloat(item.Ausbildungszulage) || 0,
        minEinkommenMonat: parseFloat(item.MinEinkommenMonat) || 0,
      };
    case "suva":
      return {
        ...base,
        nbuAN_M: parseFloat(item.NbuAN_M) || 0, nbuAN_W: parseFloat(item.NbuAN_W) || 0,
        nbuAG_M: parseFloat(item.NbuAG_M) || 0, nbuAG_W: parseFloat(item.NbuAG_W) || 0,
        buAG_M: parseFloat(item.BuAG_M) || 0, buAG_W: parseFloat(item.BuAG_W) || 0,
        maxVersicherterLohn: parseFloat(item.MaxVersicherterLohn) || 0,
      };
    case "ktg":
      return {
        ...base,
        satzAN_M: parseFloat(item.SatzAN_M) || 0, satzAN_W: parseFloat(item.SatzAN_W) || 0,
        satzAG_M: parseFloat(item.SatzAG_M) || 0, satzAG_W: parseFloat(item.SatzAG_W) || 0,
        maxVersicherterLohn: parseFloat(item.MaxVersicherterLohn) || 0,
      };
    case "pk":
      return {
        ...base,
        berechnungsModus: item.BerechnungsModus === "manuell" ? "manuell" : "formel",
        koordinationsabzug: parseFloat(item.Koordinationsabzug) || 0,
        eintrittsschwelle: parseFloat(item.Eintrittsschwelle) || 0,
        maxVersicherterLohn: parseFloat(item.MaxVersicherterLohn) || 0,
        anteilAG: parseFloat(item.AnteilAG) || 50,
        alterstabelleMaenner: item.AlterstabelleMaenner ? JSON.parse(item.AlterstabelleMaenner) : [],
        alterstabelleFrauen: item.AlterstabelleFrauen ? JSON.parse(item.AlterstabelleFrauen) : [],
      };
    default:
      return base;
  }
};

// Map JS object back to Azure entity for write
const mapRateToEntity = (type, data) => {
  const base = {
    GueltigAb: data.gueltigAb || "",
    Zahlungsrhythmus: data.zahlungsrhythmus || "monatlich",
    JaehrlichEndMonat: data.jaehrlichEndMonat || 12,
    Bemerkung: data.bemerkung || "",
  };

  switch (type) {
    case "ahv":
      return { ...base, SatzAN: parseFloat(data.satzAN) || 0, SatzAG: parseFloat(data.satzAG) || 0 };
    case "fak":
      return {
        ...base,
        SatzAG: parseFloat(data.satzAG) || 0,
        Kinderzulage: parseFloat(data.kinderzulage) || 0,
        Ausbildungszulage: parseFloat(data.ausbildungszulage) || 0,
        MinEinkommenMonat: parseFloat(data.minEinkommenMonat) || 0,
      };
    case "suva":
      return {
        ...base,
        NbuAN_M: parseFloat(data.nbuAN_M) || 0, NbuAN_W: parseFloat(data.nbuAN_W) || 0,
        NbuAG_M: parseFloat(data.nbuAG_M) || 0, NbuAG_W: parseFloat(data.nbuAG_W) || 0,
        BuAG_M: parseFloat(data.buAG_M) || 0, BuAG_W: parseFloat(data.buAG_W) || 0,
        MaxVersicherterLohn: parseFloat(data.maxVersicherterLohn) || 0,
      };
    case "ktg":
      return {
        ...base,
        SatzAN_M: parseFloat(data.satzAN_M) || 0, SatzAN_W: parseFloat(data.satzAN_W) || 0,
        SatzAG_M: parseFloat(data.satzAG_M) || 0, SatzAG_W: parseFloat(data.satzAG_W) || 0,
        MaxVersicherterLohn: parseFloat(data.maxVersicherterLohn) || 0,
      };
    case "pk":
      return {
        ...base,
        BerechnungsModus: data.berechnungsModus === "manuell" ? "manuell" : "formel",
        Koordinationsabzug: parseFloat(data.koordinationsabzug) || 0,
        Eintrittsschwelle: parseFloat(data.eintrittsschwelle) || 0,
        MaxVersicherterLohn: parseFloat(data.maxVersicherterLohn) || 0,
        AnteilAG: parseFloat(data.anteilAG) || 50,
        AlterstabelleMaenner: JSON.stringify(data.alterstabelleMaenner || []),
        AlterstabelleFrauen: JSON.stringify(data.alterstabelleFrauen || []),
      };
    default:
      return base;
  }
};

// Load ALL insurance rate entries (all types)
export const getAllInsuranceRates = async () => {
  try {
    const baseUrl = getBaseUrl();
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}()?${INSURANCE_CONFIG.sasToken}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries.map(mapEntityToRate).sort((a, b) =>
      a.gueltigAb > b.gueltigAb ? -1 : 1
    );
  } catch {
    return [];
  }
};

// Load rates for a specific insurance type
export const getInsuranceRatesByType = async (type) => {
  try {
    const baseUrl = getBaseUrl();
    const filter = `&$filter=PartitionKey eq '${type}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}()?${INSURANCE_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries.map(mapEntityToRate).sort((a, b) =>
      a.gueltigAb > b.gueltigAb ? -1 : 1
    );
  } catch {
    return [];
  }
};

// Get the active rate for a type at a specific date (latest GueltigAb <= stichtag)
export const getActiveRates = async (type, stichtag) => {
  const entries = await getInsuranceRatesByType(type);
  return entries.find((e) => e.gueltigAb <= stichtag) || null;
};

// Get active rates for ALL types at a specific date
export const getAllActiveRates = async (stichtag) => {
  const allRates = await getAllInsuranceRates();
  const result = {};
  for (const type of Object.keys(INSURANCE_TYPES)) {
    const typeEntries = allRates.filter((e) => e.type === type);
    result[type] = typeEntries.find((e) => e.gueltigAb <= stichtag) || null;
  }
  return result;
};

// Create a new insurance rate entry
export const createInsuranceRate = async (type, data) => {
  try {
    await ensureTableExists();

    const rowKey = generateInsuranceId();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: type,
      RowKey: rowKey,
      ...mapRateToEntity(type, data),
      CreatedAt: now,
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}?${INSURANCE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Create insurance rate failed: ${response.status} - ${errorText}`);
    }

    return { id: rowKey, type, ...data, createdAt: now, updatedAt: now };
  } catch (error) {
    throw error;
  }
};

// Update an existing insurance rate entry
export const updateInsuranceRate = async (type, rowKey, data) => {
  try {
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: type,
      RowKey: rowKey,
      ...mapRateToEntity(type, data),
      CreatedAt: data.createdAt || now,
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}(PartitionKey='${type}',RowKey='${rowKey}')?${INSURANCE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Update insurance rate failed: ${response.status} - ${errorText}`);
    }

    return { id: rowKey, type, ...data, updatedAt: now };
  } catch (error) {
    throw error;
  }
};

// Ensure a default FAK rate entry exists (AKBS Basel-Stadt 2026)
export const ensureDefaultFAKRate = async () => {
  const existing = await getInsuranceRatesByType("fak");
  if (existing.length > 0) return existing[0];
  return createInsuranceRate("fak", {
    gueltigAb: "2026-01-01",
    satzAG: 1.65,
    kinderzulage: 275,
    ausbildungszulage: 325,
    minEinkommenMonat: 630,
    zahlungsrhythmus: "monatlich",
    bemerkung: "AKBS Basel-Stadt (Standard)",
  });
};

// Ensure a default SUVA rate entry exists
export const ensureDefaultSUVARate = async () => {
  const existing = await getInsuranceRatesByType("suva");
  if (existing.length > 0) return existing[0];
  return createInsuranceRate("suva", {
    gueltigAb: "2026-01-01",
    nbuAN_M: 0.89, nbuAN_W: 0.89,
    nbuAG_M: 0, nbuAG_W: 0,
    buAG_M: 0.1287, buAG_W: 0.1287,
    maxVersicherterLohn: 148200,
    zahlungsrhythmus: "quartalsweise",
    bemerkung: "SUVA Standard 2026",
  });
};

// Ensure a default KTG rate entry exists
export const ensureDefaultKTGRate = async () => {
  const existing = await getInsuranceRatesByType("ktg");
  if (existing.length > 0) return existing[0];
  return createInsuranceRate("ktg", {
    gueltigAb: "2026-01-01",
    satzAN_M: 0.50, satzAN_W: 0.50,
    satzAG_M: 0.70, satzAG_W: 0.70,
    maxVersicherterLohn: 148200,
    zahlungsrhythmus: "quartalsweise",
    bemerkung: "KTG Standard 2026",
  });
};

// Ensure a default PK rate entry exists (Stiftung Abendrot)
export const ensureDefaultPKRate = async () => {
  const existing = await getInsuranceRatesByType("pk");
  if (existing.length > 0) return existing[0];
  return createInsuranceRate("pk", {
    gueltigAb: "2026-01-01",
    koordinationsabzug: 26460,
    eintrittsschwelle: 22680,
    maxVersicherterLohn: 90720,
    anteilAG: 60,
    alterstabelleMaenner: [
      { alter: 25, satz: 9.00 }, { alter: 26, satz: 9.00 }, { alter: 27, satz: 9.00 },
      { alter: 28, satz: 9.00 }, { alter: 29, satz: 9.00 }, { alter: 30, satz: 9.00 },
      { alter: 31, satz: 9.00 }, { alter: 32, satz: 9.00 }, { alter: 33, satz: 9.00 },
      { alter: 34, satz: 9.00 }, { alter: 35, satz: 12.00 }, { alter: 36, satz: 12.00 },
      { alter: 37, satz: 12.00 }, { alter: 38, satz: 12.00 }, { alter: 39, satz: 12.00 },
      { alter: 40, satz: 12.00 }, { alter: 41, satz: 12.00 }, { alter: 42, satz: 12.00 },
      { alter: 43, satz: 12.00 }, { alter: 44, satz: 12.00 }, { alter: 45, satz: 16.00 },
      { alter: 46, satz: 16.00 }, { alter: 47, satz: 16.00 }, { alter: 48, satz: 16.00 },
      { alter: 49, satz: 16.00 }, { alter: 50, satz: 16.00 }, { alter: 51, satz: 16.00 },
      { alter: 52, satz: 16.00 }, { alter: 53, satz: 16.00 }, { alter: 54, satz: 16.00 },
      { alter: 55, satz: 19.00 }, { alter: 56, satz: 19.00 }, { alter: 57, satz: 19.00 },
      { alter: 58, satz: 19.00 }, { alter: 59, satz: 19.00 }, { alter: 60, satz: 19.00 },
      { alter: 61, satz: 19.00 }, { alter: 62, satz: 19.00 }, { alter: 63, satz: 19.00 },
      { alter: 64, satz: 19.00 }, { alter: 65, satz: 19.00 },
    ],
    alterstabelleFrauen: [
      { alter: 25, satz: 9.00 }, { alter: 26, satz: 9.00 }, { alter: 27, satz: 9.00 },
      { alter: 28, satz: 9.00 }, { alter: 29, satz: 9.00 }, { alter: 30, satz: 9.00 },
      { alter: 31, satz: 9.00 }, { alter: 32, satz: 9.00 }, { alter: 33, satz: 9.00 },
      { alter: 34, satz: 9.00 }, { alter: 35, satz: 12.00 }, { alter: 36, satz: 12.00 },
      { alter: 37, satz: 12.00 }, { alter: 38, satz: 12.00 }, { alter: 39, satz: 12.00 },
      { alter: 40, satz: 12.00 }, { alter: 41, satz: 12.00 }, { alter: 42, satz: 12.00 },
      { alter: 43, satz: 12.00 }, { alter: 44, satz: 12.00 }, { alter: 45, satz: 16.00 },
      { alter: 46, satz: 16.00 }, { alter: 47, satz: 16.00 }, { alter: 48, satz: 16.00 },
      { alter: 49, satz: 16.00 }, { alter: 50, satz: 16.00 }, { alter: 51, satz: 16.00 },
      { alter: 52, satz: 16.00 }, { alter: 53, satz: 16.00 }, { alter: 54, satz: 16.00 },
      { alter: 55, satz: 19.00 }, { alter: 56, satz: 19.00 }, { alter: 57, satz: 19.00 },
      { alter: 58, satz: 19.00 }, { alter: 59, satz: 19.00 }, { alter: 60, satz: 19.00 },
      { alter: 61, satz: 19.00 }, { alter: 62, satz: 19.00 }, { alter: 63, satz: 19.00 },
      { alter: 64, satz: 19.00 },
    ],
    zahlungsrhythmus: "monatlich",
    bemerkung: "Stiftung Abendrot (Standard)",
  });
};

// Delete an insurance rate entry (only allowed for future entries)
export const deleteInsuranceRate = async (type, rowKey) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='${type}',RowKey='${rowKey}')?${INSURANCE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Delete insurance rate failed: ${response.status}`);
    }
    return true;
  } catch (error) {
    throw error;
  }
};
